import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import type { PromptStatus, Site } from '@shared/types'
import { useToday } from '../state/today'
import { useToast } from '../state/toast'
import { DayRail } from '../components/DayRail'
import { EnableReminders } from '../components/EnableReminders'
import { NoteCard } from '../components/NoteCard'
import { Button, EmptyState, Sheet, Spinner, SectionHeading } from '../components/ui'
import { formatDuration, localDateKey, pluralise } from '../lib/format'
import { put } from '../lib/api'
import { getCurrentFix, nearestSite } from '../lib/geo'
import { AddSiteSheet } from '../components/AddSiteSheet'

export default function TodayPage() {
  const { state, loading, busy, clockIn, clockOut, switchSite, refresh } = useToday()
  const toast = useToast()
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerMode, setPickerMode] = useState<'in' | 'switch'>('in')

  // A tapped reminder can deep-link straight into the action it wants.
  useEffect(() => {
    const prompt = params.get('prompt')
    if (!prompt || !state) return
    if (prompt === 'clock_in' && !state.openEntry) {
      setPickerMode('in')
      setPickerOpen(true)
    }
    if (prompt === 'clock_out' && state.openEntry) {
      void handleClockOut()
    }
    params.delete('prompt')
    params.delete('schedule')
    params.delete('site')
    setParams(params, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, params])

  if (loading && !state) {
    return (
      <div className="py-16">
        <Spinner label="Loading your day" />
      </div>
    )
  }
  if (!state) return null

  const open = state.openEntry
  const outstanding = state.outstandingInventory
  const unpostedPrompts = state.prompts.filter((p) => !p.done && p.action === 'progress_note')

  async function handleClockOut() {
    if (!state) return
    // Clocking out with gear still signed out is the single most common way
    // tools go missing, so it gets a sentence, not a silent success.
    if (outstanding.length > 0) {
      const total = outstanding.reduce((n, row) => n + row.qtyOut, 0)
      toast.warn(`You still have ${pluralise(total, 'item')} signed out.`)
    }
    try {
      await clockOut()
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not clock out')
    }
  }

  async function handlePick(site: Site) {
    setPickerOpen(false)
    try {
      if (pickerMode === 'switch') await switchSite(site.id)
      else await clockIn(site.id)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not update your clock')
    }
  }

  function handlePrompt(prompt: PromptStatus) {
    if (prompt.action === 'clock_in') {
      setPickerMode('in')
      setPickerOpen(true)
      return
    }
    if (prompt.action === 'clock_out') {
      // End-of-day usually wants the final photo first.
      if (prompt.noteType && !state?.notes.some((n) => n.scheduleId === prompt.scheduleId)) {
        navigate(`/note/new?schedule=${prompt.scheduleId}&then=clock-out`)
        return
      }
      void handleClockOut()
      return
    }
    navigate(`/note/new?schedule=${prompt.scheduleId}`)
  }

  const todayKey = localDateKey(new Date(), state.timezone)
  const isToday = localDateKey(new Date(state.now), state.timezone) === todayKey

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <p className="label">
            {new Intl.DateTimeFormat('en-CA', {
              timeZone: state.timezone, weekday: 'long', month: 'long', day: 'numeric',
            }).format(new Date(state.now))}
          </p>
          <h1 className="mt-0.5 text-3xl">
            {state.user.name.split(' ')[0]}
          </h1>
        </div>
        <div className="text-right">
          <p className="label">On site today</p>
          <p className="font-mono text-2xl font-semibold tnum">
            {formatDuration(state.totalWorkedMs)}
          </p>
        </div>
      </header>

      {/* Primary action. One button, always the next thing to do. */}
      {open ? (
        <div className="space-y-2">
          <Button
            variant="primary"
            size="lg"
            className="w-full"
            loading={busy}
            onClick={() => void handleClockOut()}
          >
            Clock out of {open.siteName}
          </Button>
          <Button
            variant="secondary"
            className="w-full"
            disabled={busy}
            onClick={() => {
              setPickerMode('switch')
              setPickerOpen(true)
            }}
          >
            Move to another site
          </Button>
        </div>
      ) : (
        <Button
          variant="primary"
          size="lg"
          className="w-full"
          loading={busy}
          onClick={() => {
            setPickerMode('in')
            setPickerOpen(true)
          }}
        >
          Clock in
        </Button>
      )}

      <div className="grid grid-cols-2 gap-2">
        <Button className="w-full" onClick={() => navigate('/note/new')}>
          Post an update
        </Button>
        <Button className="w-full" onClick={() => navigate('/inventory')}>
          {outstanding.length > 0
            ? `Gear (${outstanding.reduce((n, r) => n + r.qtyOut, 0)} out)`
            : 'Gear'}
        </Button>
      </div>

      {/* Sits above the day, because a day with no reminders is the whole
          problem this app is meant to solve. */}
      <EnableReminders />

      <section>
        <SectionHeading
          action={
            state.entries.length > 0 ? (
              <button
                type="button"
                onClick={() => navigate('/log?tab=hours')}
                className="link text-xs"
              >
                Fix a time
              </button>
            ) : undefined
          }
        >
          The day
        </SectionHeading>
        {state.entries.length === 0 && state.prompts.length === 0 ? (
          <EmptyState
            title="Nothing scheduled"
            body="Clock in when you get to a site and your day will build itself here."
          />
        ) : (
          <DayRail
            entries={[...state.entries].reverse()}
            prompts={state.prompts}
            timezone={state.timezone}
            now={state.now}
            isToday={isToday}
            onPrompt={handlePrompt}
          />
        )}
      </section>

      {unpostedPrompts.length > 0 && (
        <p className="text-sm text-graphite-2">
          {pluralise(unpostedPrompts.length, 'update')} still to post today.
        </p>
      )}

      <section>
        <SectionHeading
          action={
            state.notes.length > 0 ? (
              <button
                type="button"
                onClick={() => navigate('/log')}
                className="link text-xs"
              >
                See all
              </button>
            ) : undefined
          }
        >
          Today’s updates
        </SectionHeading>

        {state.notes.length === 0 ? (
          <EmptyState
            title="No updates yet"
            body="A photo and a line of text is enough. The office sees it right away."
            action={
              <Button variant="primary" onClick={() => navigate('/note/new')}>
                Post an update
              </Button>
            }
          />
        ) : (
          <div className="space-y-3">
            {state.notes.slice(0, 5).map((note) => (
              <NoteCard
                key={note.id}
                note={note}
                noteTypes={state.noteTypes}
                timezone={state.timezone}
                sites={state.sites}
                onSiteChanged={() => void refresh()}
              />
            ))}
          </div>
        )}
      </section>

      <SitePickerSheet
        open={pickerOpen}
        mode={pickerMode}
        sites={state.sites}
        currentSiteId={open?.siteId ?? null}
        onClose={() => setPickerOpen(false)}
        onPick={handlePick}
      />
    </div>
  )
}

/**
 * Site picker. Sorted by distance when we can get a fix, because the site you
 * are standing on should be the first thing under your thumb. A job that is
 * not in the list yet can be added from the bottom of it; a site somebody
 * added without a location can have one set by whoever is standing on it.
 */
function SitePickerSheet({
  open, mode, sites, currentSiteId, onClose, onPick,
}: {
  open: boolean
  mode: 'in' | 'switch'
  sites: Site[]
  currentSiteId: string | null
  onClose: () => void
  onPick: (site: Site) => void
}) {
  const { refresh } = useToday()
  const toast = useToast()
  const [distances, setDistances] = useState<Record<string, number>>({})
  const [locating, setLocating] = useState(false)
  const [adding, setAdding] = useState(false)
  const [settingLocation, setSettingLocation] = useState<string | null>(null)

  const setLocationHere = async (site: Site) => {
    setSettingLocation(site.id)
    try {
      const fix = await getCurrentFix({ timeout: 10_000 })
      await put(`/api/sites/${site.id}/location`, { lat: fix.lat, lng: fix.lng })
      await refresh()
      toast.success(`${site.name} now has a location`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Could not set the location')
    } finally {
      setSettingLocation(null)
    }
  }

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setLocating(true)
    getCurrentFix({ timeout: 8000 })
      .then((fix) => {
        if (cancelled) return
        const next: Record<string, number> = {}
        for (const site of sites) {
          if (site.lat == null || site.lng == null) continue
          const hit = nearestSite([site], fix)
          if (hit) next[site.id] = hit.distanceM
        }
        setDistances(next)
      })
      .catch(() => undefined)
      .finally(() => !cancelled && setLocating(false))
    return () => {
      cancelled = true
    }
  }, [open, sites])

  const ordered = useMemo(() => {
    const list = sites.filter((s) => s.id !== currentSiteId || mode === 'in')
    return [...list].sort((a, b) => {
      const da = distances[a.id]
      const db = distances[b.id]
      if (da != null && db != null) return da - db
      if (da != null) return -1
      if (db != null) return 1
      return a.name.localeCompare(b.name)
    })
  }, [sites, distances, currentSiteId, mode])

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={mode === 'switch' ? 'Move to which site?' : 'Which site?'}
    >
      {locating && <p className="label mb-3">Finding the closest site…</p>}
      <div className="space-y-2">
        {ordered.map((site) => {
          const distance = distances[site.id]
          const inside = distance != null && distance <= site.radiusM
          const noLocation = site.lat == null || site.lng == null
          return (
            <div key={site.id} className="card">
              <button
                type="button"
                onClick={() => onPick(site)}
                className="flex w-full items-center gap-3 p-3 text-left"
              >
                <span
                  className="size-3 shrink-0 rounded-full"
                  style={{ background: site.color }}
                  aria-hidden
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-bold">{site.name}</span>
                  {site.address && (
                    <span className="block truncate text-xs text-graphite-2">{site.address}</span>
                  )}
                </span>
                {distance != null && (
                  <span
                    className={`chip ${inside ? 'bg-ink text-brand' : 'text-graphite-2'}`}
                    style={inside ? undefined : { boxShadow: 'inset 0 0 0 1px var(--wm-line-strong)' }}
                  >
                    {inside ? 'here' : formatDistance(distance)}
                  </span>
                )}
              </button>
              {noLocation && (
                <div className="flex items-center justify-between gap-2 border-t border-line px-3 py-1.5">
                  <span className="text-xs text-graphite-2">No location yet</span>
                  <Button
                    className="min-h-9 px-3 text-xs"
                    loading={settingLocation === site.id}
                    onClick={() => void setLocationHere(site)}
                  >
                    Set location here
                  </Button>
                </div>
              )}
            </div>
          )
        })}
        {ordered.length === 0 && (
          <EmptyState title="No sites yet" body="Add the site you are on below." />
        )}
        <Button className="w-full" onClick={() => setAdding(true)}>
          + Add a new site
        </Button>
      </div>
      {adding && (
        <AddSiteSheet
          onClose={() => setAdding(false)}
          onPicked={(site) => {
            setAdding(false)
            onPick(site)
          }}
        />
      )}
    </Sheet>
  )
}

function formatDistance(metres: number): string {
  if (metres < 1000) return `${Math.round(metres / 10) * 10} m`
  return `${(metres / 1000).toFixed(1)} km`
}
